import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const AnimatedBackground = ({ variant = 'default', className = '' }) => {
  const [currentBg, setCurrentBg] = useState(0);

  // Sophisticated animated backgrounds from various sources
  const backgrounds = {
    default: [
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Space/tech
      'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Network
    ],
    particles: [
      'https://cdn.pixabay.com/photo/2016/11/30/20/58/programming-1873854_1280.png',
      'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    ],
    neural: [
      'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1639322537228-f710d846310a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    ],
    matrix: [
      'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    ]
  };

  // Cycle through backgrounds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBg(prev => (prev + 1) % backgrounds[variant].length);
    }, 8000);
    return () => clearInterval(interval);
  }, [variant]);

  return (
    <div className={`fixed inset-0 -z-10 ${className}`}>
      {/* Animated CSS Background */}
      <div className="absolute inset-0 animated-gradient-bg"></div>
      
      {/* Floating Particles */}
      <div className="absolute inset-0 particles-container">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Neural Network Animation */}
      <div className="absolute inset-0 neural-network">
        <svg className="w-full h-full opacity-20">
          <defs>
            <linearGradient id="networkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          
          {/* Animated network nodes */}
          {[...Array(12)].map((_, i) => (
            <motion.circle
              key={i}
              cx={`${20 + (i % 4) * 20}%`}
              cy={`${20 + Math.floor(i / 4) * 30}%`}
              r="3"
              fill="url(#networkGradient)"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: [0, 1, 0.5, 1],
                scale: [0, 1, 1.2, 1],
              }}
              transition={{
                duration: 4,
                delay: i * 0.3,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
          ))}
          
          {/* Animated connections */}
          {[...Array(8)].map((_, i) => (
            <motion.line
              key={`line-${i}`}
              x1={`${20 + (i % 4) * 20}%`}
              y1={`${20 + Math.floor(i / 4) * 30}%`}
              x2={`${20 + ((i + 1) % 4) * 20}%`}
              y2={`${20 + Math.floor((i + 1) / 4) * 30}%`}
              stroke="url(#networkGradient)"
              strokeWidth="1"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ 
                pathLength: [0, 1, 0],
                opacity: [0, 0.6, 0]
              }}
              transition={{
                duration: 3,
                delay: i * 0.5,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
          ))}
        </svg>
      </div>

      {/* Geometric Shapes Animation */}
      <div className="absolute inset-0 geometric-shapes">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-16 h-16 border border-primary-500/20 backdrop-blur-sm"
            style={{
              left: `${10 + (i % 4) * 25}%`,
              top: `${10 + Math.floor(i / 4) * 40}%`,
              borderRadius: i % 2 === 0 ? '50%' : '0%'
            }}
            animate={{
              rotate: [0, 360],
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.7, 0.3]
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>

      {/* Wave Animation */}
      <div className="absolute bottom-0 left-0 w-full wave-container">
        <svg viewBox="0 0 1200 120" className="w-full h-32">
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,60 C300,120 900,0 1200,60 L1200,120 L0,120 Z"
            fill="url(#waveGradient)"
            animate={{
              d: [
                "M0,60 C300,120 900,0 1200,60 L1200,120 L0,120 Z",
                "M0,80 C300,20 900,100 1200,40 L1200,120 L0,120 Z",
                "M0,60 C300,120 900,0 1200,60 L1200,120 L0,120 Z"
              ]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </svg>
      </div>

      {/* Data Stream Animation */}
      <div className="absolute inset-0 data-stream opacity-30">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 bg-gradient-to-b from-primary-400 to-transparent"
            style={{
              left: `${15 + i * 15}%`,
              height: '100%'
            }}
            animate={{
              opacity: [0, 1, 0],
              scaleY: [0, 1, 0]
            }}
            transition={{
              duration: 2,
              delay: i * 0.3,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
      </div>

      {/* Overlay gradients for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-950/50 via-transparent to-dark-950/80"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-dark-950/30 via-transparent to-dark-950/30"></div>
    </div>
  );
};

export default AnimatedBackground;